import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import apiService from '../apiService';

function Contact() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        topic: 'Booking & Services',
        message: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submittedTicket, setSubmittedTicket] = useState(null);
    const [openFaq, setOpenFaq] = useState(0);

    const supportTopics = [
        'Booking & Services',
        'Become a Partner',
        'Billing & Payments',
        'Technician Inquiry',
        'General Feedback'
    ];

    const contactChannels = [
        {
            icon: '📞',
            title: 'Customer Helpline',
            subtitle: 'Mon - Sun, 8:00 AM - 10:00 PM',
            contact: '+91 800-RELIEP (735-437)',
            link: 'tel:+91800735437',
            action: 'Call Helpline'
        },
        {
            icon: '💬',
            title: 'WhatsApp Fast Care',
            subtitle: 'Instant chat with support executive',
            contact: 'Average reply time: < 3 mins',
            link: 'https://wa.me/919876543210?text=Hello%20Reliep%20Support',
            action: 'Chat on WhatsApp'
        },
        {
            icon: '✉️',
            title: 'Official Email Support',
            subtitle: 'For booking queries & receipts',
            contact: 'support@reliep.com',
            link: 'mailto:support@reliep.com',
            action: 'Send an Email'
        },
        {
            icon: '🤝',
            title: 'Partner & Business Desk',
            subtitle: 'Join as a vendor or contractor',
            contact: 'partner@reliep.com',
            link: 'mailto:partner@reliep.com',
            action: 'Partner Inquiries'
        }
    ];

    const faqs = [
        {
            q: 'How quickly will a service expert arrive after booking?',
            a: 'For on-demand services (electricians, plumbers, fast cabs), an expert is dispatched immediately and typically arrives at your doorstep in 30 to 45 minutes. For scheduled maintenance, you can choose your preferred time slot.'
        },
        {
            q: 'What is the Reliep 100% Satisfaction Guarantee?',
            a: 'If you are not completely satisfied with the quality of the service provided, Reliep provides free re-work by an advanced supervisor or a hassle-free refund within 24 hours of inspection.'
        },
        {
            q: 'How do I pay for my service?',
            a: 'You can pay online securely using UPI, Credit/Debit cards, or Net Banking, or pay directly via Cash / UPI QR code to the technician upon job completion.'
        },
        {
            q: 'How do I register as a service partner or technician?',
            a: 'You can register in under 3 minutes by clicking "Become a Partner" in our header or visiting /become-a-partner. Our onboarding team verifies your KYC and gets you live within 24 hours.'
        },
        {
            q: 'Can I reschedule or cancel my booking?',
            a: 'Yes, you can reschedule or cancel directly from your profile or by contacting our 24/7 care desk before the technician starts travel with zero cancellation fee.'
        }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
            alert('Please fill out all required fields.');
            return;
        }

        setIsSubmitting(true);
        const ticketId = 'RLP-' + Math.floor(100000 + Math.random() * 900000);

        try {
            // Attempt to send to backend feedback endpoint
            await apiService.feedback_submit({
                user_id: 1,
                feedback: `[Contact Form - ${formData.topic}] From: ${formData.name} (${formData.email}, Phone: ${formData.phone || 'N/A'})\n\nMessage:\n${formData.message}`
            });
        } catch (err) {
            console.warn('Feedback API note:', err?.message || err);
        } finally {
            setIsSubmitting(false);
            setSubmittedTicket({
                id: ticketId,
                name: formData.name,
                email: formData.email,
                topic: formData.topic
            });
            setFormData({
                name: '',
                email: '',
                phone: '',
                topic: 'Booking & Services',
                message: ''
            });
        }
    };

    return (
        <div style={{ backgroundColor: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Helmet>
                <title>Contact Us | Reliep - 24/7 Customer & Partner Support</title>
                <meta name="description" content="Get in touch with Reliep support. Reach us via phone, WhatsApp, email, or our priority contact form for fast doorstep service support." />
            </Helmet>

            <Navbar />

            {/* HERO SECTION */}
            <section style={{
                background: 'radial-gradient(circle at 85% 20%, #fbf0fb 0%, #ffffff 65%)',
                padding: '64px 20px 72px',
                borderBottom: '1px solid #f2e4f2',
                textAlign: 'center'
            }}>
                <div style={{ maxWidth: '840px', margin: '0 auto' }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: '#fbf2fb',
                        border: '1px solid #ebd0eb',
                        padding: '6px 16px',
                        borderRadius: '9999px',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        color: '#850C85',
                        marginBottom: '20px'
                    }}>
                        <span>✨</span>
                        <span>We’re Always Here For You</span>
                    </div>

                    <h1 style={{
                        fontSize: 'clamp(2.2rem, 4.5vw, 3.2rem)',
                        fontWeight: 800,
                        color: '#0f172a',
                        lineHeight: 1.2,
                        letterSpacing: '-0.8px',
                        marginBottom: '16px'
                    }}>
                        Get in Touch with{' '}
                        <span style={{
                            color: '#850C85',
                            background: 'linear-gradient(135deg, #850C85 0%, #b81ab8 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            Reliep Care
                        </span>
                    </h1>

                    <p style={{
                        fontSize: '1.08rem',
                        color: '#475569',
                        lineHeight: 1.6,
                        margin: '0 auto',
                        maxWidth: '680px'
                    }}>
                        Have questions about a booking, want to partner with us, or need technical assistance? Our dedicated customer care team is available round the clock.
                    </p>
                </div>
            </section>

            {/* QUICK CONTACT CHANNELS */}
            <section style={{ padding: '60px 20px 40px', backgroundColor: '#faf8fa' }}>
                <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                        gap: '20px'
                    }}>
                        {contactChannels.map((item, idx) => (
                            <div
                                key={idx}
                                style={{
                                    backgroundColor: '#ffffff',
                                    borderRadius: '16px',
                                    padding: '28px 24px',
                                    border: '1px solid #f1e2f1',
                                    boxShadow: '0 4px 16px rgba(133, 12, 133, 0.05)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.borderColor = '#850C85';
                                    e.currentTarget.style.boxShadow = '0 16px 32px rgba(133, 12, 133, 0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.borderColor = '#f1e2f1';
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(133, 12, 133, 0.05)';
                                }}
                            >
                                <div style={{
                                    width: '52px',
                                    height: '52px',
                                    borderRadius: '14px',
                                    background: '#fbf2fb',
                                    color: '#850C85',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.6rem',
                                    marginBottom: '18px'
                                }}>
                                    {item.icon}
                                </div>

                                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>
                                    {item.title}
                                </h3>

                                <span style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '14px', display: 'block' }}>
                                    {item.subtitle}
                                </span>

                                <span style={{
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    marginBottom: '20px',
                                    wordBreak: 'break-word'
                                }}>
                                    {item.contact}
                                </span>

                                <a
                                    href={item.link}
                                    target={item.link.startsWith('http') ? '_blank' : undefined}
                                    rel="noreferrer"
                                    style={{
                                        marginTop: 'auto',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        color: '#850C85',
                                        textDecoration: 'none',
                                        fontWeight: 700,
                                        fontSize: '0.88rem'
                                    }}
                                >
                                    <span>{item.action}</span>
                                    <span>→</span>
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FORM + GUARANTEE SECTION */}
            <section style={{ padding: '60px 20px 80px', backgroundColor: '#ffffff' }}>
                <div style={{
                    maxWidth: '1280px',
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(320px, 1fr) minmax(320px, 1.2fr)',
                    gap: '48px',
                    alignItems: 'start'
                }}>
                    {/* LEFT COLUMN: INFORMATION & SLA */}
                    <div>
                        <span style={{
                            fontSize: '0.82rem',
                            fontWeight: 800,
                            color: '#850C85',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            display: 'block',
                            marginBottom: '8px'
                        }}>
                            DIRECT ASSISTANCE
                        </span>

                        <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.25, marginBottom: '18px' }}>
                            Send us a message and we'll reply within 30 minutes.
                        </h2>

                        <p style={{ fontSize: '1rem', color: '#64748b', lineHeight: 1.6, marginBottom: '32px' }}>
                            Whether it's tracking an ongoing technician, custom business bookings, or feedback, our executive team handles every ticket personally.
                        </p>

                        {/* SLA Metrics */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '16px',
                            marginBottom: '36px'
                        }}>
                            <div style={{
                                background: '#fbf2fb',
                                padding: '20px',
                                borderRadius: '14px',
                                border: '1px solid #ebd0eb'
                            }}>
                                <span style={{ fontSize: '1.8rem', fontWeight: 800, color: '#850C85', display: 'block' }}>
                                    &lt; 30s
                                </span>
                                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', display: 'block', marginTop: '4px' }}>
                                    Average Call Pickup
                                </span>
                                <span style={{ fontSize: '0.74rem', color: '#64748b' }}>Direct human support</span>
                            </div>

                            <div style={{
                                background: '#fbf2fb',
                                padding: '20px',
                                borderRadius: '14px',
                                border: '1px solid #ebd0eb'
                            }}>
                                <span style={{ fontSize: '1.8rem', fontWeight: 800, color: '#850C85', display: 'block' }}>
                                    99.4%
                                </span>
                                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', display: 'block', marginTop: '4px' }}>
                                    Resolution Rate
                                </span>
                                <span style={{ fontSize: '0.74rem', color: '#64748b' }}>First-contact resolved</span>
                            </div>
                        </div>

                        {/* Reliep Office Address Card */}
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            border: '1px solid #f1e2f1',
                            padding: '24px',
                            boxShadow: '0 4px 16px rgba(133, 12, 133, 0.05)',
                            display: 'flex',
                            gap: '16px',
                            alignItems: 'flex-start'
                        }}>
                            <div style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                background: '#fbf2fb',
                                color: '#850C85',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.3rem',
                                flexShrink: 0
                            }}>
                                🏢
                            </div>
                            <div>
                                <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>
                                    Reliep Operational Headquarters
                                </h4>
                                <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.5, margin: 0 }}>
                                    Reliep Technologies Private Limited<br />
                                    Technology Park, Sector V, Salt Lake City<br />
                                    Kolkata, West Bengal - 700091, India
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: INTERACTIVE FORM */}
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '24px',
                        border: '1.5px solid #ebd0eb',
                        boxShadow: '0 20px 45px rgba(133, 12, 133, 0.08)',
                        padding: '36px'
                    }}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', margin: '0 0 8px' }}>
                            Send a Priority Message
                        </h3>
                        <p style={{ fontSize: '0.9rem', color: '#64748b', margin: '0 0 24px' }}>
                            Fill out the details below and an executive will reach out to you shortly.
                        </p>

                        {submittedTicket ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '36px 20px',
                                background: '#fbf2fb',
                                borderRadius: '16px',
                                border: '1.5px solid #ebd0eb'
                            }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    background: '#850C85',
                                    color: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.8rem',
                                    margin: '0 auto 16px'
                                }}>
                                    ✓
                                </div>
                                <h4 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
                                    Message Dispatched Successfully!
                                </h4>
                                <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '16px' }}>
                                    Thank you, <strong>{submittedTicket.name}</strong>. Your inquiry has been registered under reference:
                                </p>
                                <span style={{
                                    display: 'inline-block',
                                    background: '#ffffff',
                                    border: '1px solid #ebd0eb',
                                    padding: '6px 16px',
                                    borderRadius: '8px',
                                    fontWeight: 800,
                                    fontSize: '1.1rem',
                                    color: '#850C85',
                                    letterSpacing: '1px',
                                    marginBottom: '24px'
                                }}>
                                    {submittedTicket.id}
                                </span>
                                <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '24px' }}>
                                    A confirmation notice has been sent to <strong>{submittedTicket.email}</strong>.
                                </p>
                                <button
                                    onClick={() => setSubmittedTicket(null)}
                                    style={{
                                        background: '#850C85',
                                        color: '#ffffff',
                                        border: 'none',
                                        padding: '12px 28px',
                                        borderRadius: '12px',
                                        fontWeight: 700,
                                        fontSize: '0.9rem',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 12px rgba(133, 12, 133, 0.25)'
                                    }}
                                >
                                    Send Another Message
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {/* Topic Selection Pills */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
                                        What can we help you with?
                                    </label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {supportTopics.map(topic => (
                                            <button
                                                key={topic}
                                                type="button"
                                                onClick={() => setFormData(p => ({ ...p, topic }))}
                                                style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '9999px',
                                                    border: formData.topic === topic ? '1.5px solid #850C85' : '1px solid #ebd0eb',
                                                    background: formData.topic === topic ? '#850C85' : '#ffffff',
                                                    color: formData.topic === topic ? '#ffffff' : '#475569',
                                                    fontSize: '0.82rem',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                {topic}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Name & Phone in 2 Columns */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="e.g. Rahul Sharma"
                                            required
                                            style={{
                                                width: '100%',
                                                padding: '12px 14px',
                                                borderRadius: '10px',
                                                border: '1.5px solid #ebd0eb',
                                                outline: 'none',
                                                fontSize: '0.92rem',
                                                boxSizing: 'border-box'
                                            }}
                                            onFocus={(e) => { e.target.style.borderColor = '#850C85'; }}
                                            onBlur={(e) => { e.target.style.borderColor = '#ebd0eb'; }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="+91 98765 43210"
                                            style={{
                                                width: '100%',
                                                padding: '12px 14px',
                                                borderRadius: '10px',
                                                border: '1.5px solid #ebd0eb',
                                                outline: 'none',
                                                fontSize: '0.92rem',
                                                boxSizing: 'border-box'
                                            }}
                                            onFocus={(e) => { e.target.style.borderColor = '#850C85'; }}
                                            onBlur={(e) => { e.target.style.borderColor = '#ebd0eb'; }}
                                        />
                                    </div>
                                </div>

                                {/* Email Address */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="rahul@example.com"
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 14px',
                                            borderRadius: '10px',
                                            border: '1.5px solid #ebd0eb',
                                            outline: 'none',
                                            fontSize: '0.92rem',
                                            boxSizing: 'border-box'
                                        }}
                                        onFocus={(e) => { e.target.style.borderColor = '#850C85'; }}
                                        onBlur={(e) => { e.target.style.borderColor = '#ebd0eb'; }}
                                    />
                                </div>

                                {/* Message */}
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
                                        How can we assist you? *
                                    </label>
                                    <textarea
                                        name="message"
                                        rows={4}
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Describe your query or request with any relevant booking details..."
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 14px',
                                            borderRadius: '10px',
                                            border: '1.5px solid #ebd0eb',
                                            outline: 'none',
                                            fontSize: '0.92rem',
                                            resize: 'vertical',
                                            fontFamily: 'inherit',
                                            boxSizing: 'border-box'
                                        }}
                                        onFocus={(e) => { e.target.style.borderColor = '#850C85'; }}
                                        onBlur={(e) => { e.target.style.borderColor = '#ebd0eb'; }}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    style={{
                                        background: '#850C85',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '14px 24px',
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        boxShadow: '0 6px 18px rgba(133, 12, 133, 0.3)',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    {isSubmitting ? (
                                        <span>Dispatching Message...</span>
                                    ) : (
                                        <>
                                            <span>Send Message to Support</span>
                                            <span>→</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* FREQUENTLY ASKED QUESTIONS ACCORDION */}
            <section style={{ padding: '70px 20px 90px', backgroundColor: '#faf8fa' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                        <span style={{
                            fontSize: '0.82rem',
                            fontWeight: 800,
                            color: '#850C85',
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            display: 'block',
                            marginBottom: '6px'
                        }}>
                            COMMON QUERIES
                        </span>
                        <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>
                            Frequently Asked Questions
                        </h2>
                        <p style={{ fontSize: '0.95rem', color: '#64748b', margin: 0 }}>
                            Quick answers to common questions about booking, payments, and guarantees.
                        </p>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {faqs.map((faq, index) => {
                            const isOpen = openFaq === index;
                            return (
                                <div
                                    key={index}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '14px',
                                        border: isOpen ? '1.5px solid #850C85' : '1px solid #f1e2f1',
                                        boxShadow: '0 2px 8px rgba(133, 12, 133, 0.04)',
                                        overflow: 'hidden',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <button
                                        onClick={() => setOpenFaq(isOpen ? null : index)}
                                        style={{
                                            width: '100%',
                                            padding: '20px 24px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            textAlign: 'left'
                                        }}
                                    >
                                        <span style={{ fontSize: '1.02rem', fontWeight: 700, color: '#0f172a' }}>
                                            {faq.q}
                                        </span>
                                        <span style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '50%',
                                            background: isOpen ? '#850C85' : '#fbf2fb',
                                            color: isOpen ? '#ffffff' : '#850C85',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 800,
                                            fontSize: '1rem',
                                            flexShrink: 0,
                                            marginLeft: '12px'
                                        }}>
                                            {isOpen ? '−' : '+'}
                                        </span>
                                    </button>

                                    {isOpen && (
                                        <div style={{
                                            padding: '0 24px 20px',
                                            fontSize: '0.92rem',
                                            color: '#64748b',
                                            lineHeight: 1.6,
                                            borderTop: '1px solid #f8eef8'
                                        }}>
                                            <p style={{ margin: '14px 0 0' }}>{faq.a}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

export default Contact;
